import { useId, useState, type ReactNode } from 'react'

import { Input, cn } from '@aeris/ui'

import {
  formatDecimalInput,
  parseDecimalInput,
} from '../../lib/label-template-draft.ts'

/**
 * Os controles pequenos do editor de etiqueta (F129).
 *
 * Um painel de propriedades tem trinta controles numa coluna de 20 rem; com o
 * `Field` do formulário comum (rótulo grande, dica, espaço de erro) eles não
 * cabem. Estes são a versão compacta — rótulo curto em cima, altura de 32 px —
 * e continuam ligados ao rótulo para leitor de tela.
 */

export const EditorSection = ({
  title,
  children,
}: {
  title: string
  children: ReactNode
}): ReactNode => (
  <section className="flex flex-col gap-3 border-b border-(--border-subtle) px-4 py-3">
    <h3 className="text-(length:--text-xs) font-(--weight-semibold) tracking-wide text-(--text-muted) uppercase">
      {title}
    </h3>
    {children}
  </section>
)

/**
 * Número digitado com vírgula, que só vale quando é número.
 *
 * O texto é da pessoa enquanto ela digita — "5," é o meio do caminho para
 * "5,7", e trocar isso por "5" a cada tecla tornaria impossível digitar
 * decimal. O modelo só recebe o valor quando o texto é um número aceito.
 */
export const NumberField = ({
  label,
  value,
  onChange,
  min = 0,
  max,
  integer = false,
  unit,
}: {
  label: string
  value: number
  onChange: (value: number) => void
  min?: number
  max?: number | undefined
  integer?: boolean
  unit?: string | undefined
}): ReactNode => {
  const id = useId()
  const [text, setText] = useState(formatDecimalInput(value))
  const [seen, setSeen] = useState(value)
  if (value !== seen) {
    setSeen(value)
    setText(formatDecimalInput(value))
  }

  const accepts = (candidate: number | null): candidate is number =>
    candidate !== null &&
    candidate >= min &&
    (max === undefined || candidate <= max) &&
    (!integer || Number.isInteger(candidate))

  const hasUnit = unit !== undefined && unit !== ''

  return (
    <div className="flex min-w-0 flex-col gap-1">
      <label
        htmlFor={id}
        className="text-(length:--text-xs) text-(--text-secondary)"
      >
        {label}
      </label>
      <div className="relative">
        <Input
          id={id}
          inputMode="decimal"
          value={text}
          invalid={!accepts(parseDecimalInput(text))}
          className={cn('h-8 tabular-nums', hasUnit ? 'pr-9' : '')}
          onChange={(event) => {
            setText(event.target.value)
            const next = parseDecimalInput(event.target.value)
            if (accepts(next)) {
              setSeen(next)
              onChange(next)
            }
          }}
        />
        {hasUnit ? (
          <span
            aria-hidden="true"
            className="pointer-events-none absolute inset-y-0 right-2 flex items-center text-(length:--text-xs) text-(--text-muted)"
          >
            {unit}
          </span>
        ) : null}
      </div>
    </div>
  )
}

export type SegmentOption<Value> = {
  readonly value: Value
  readonly label: ReactNode
  /** O nome falado e a dica — obrigatório, porque o rótulo pode ser um ícone. */
  readonly title: string
}

/** Um grupo de botões em que um só está ligado: papel, alinhamento, rotação. */
export const Segmented = <Value extends string | number>({
  label,
  value,
  options,
  onChange,
}: {
  label: string
  value: Value
  options: readonly SegmentOption<Value>[]
  onChange: (value: Value) => void
}): ReactNode => (
  <div
    role="group"
    aria-label={label}
    className="inline-flex w-fit gap-0.5 rounded-(--radius-md) border border-(--border-default) bg-(--surface-sunken) p-0.5"
  >
    {options.map((option) => {
      const active = option.value === value
      return (
        <button
          key={String(option.value)}
          type="button"
          title={option.title}
          aria-label={option.title}
          aria-pressed={active}
          onClick={() => {
            onChange(option.value)
          }}
          className={cn(
            'flex h-7 min-w-8 items-center justify-center rounded-(--radius-sm) px-2 text-(length:--text-sm) transition-colors duration-(--duration-fast)',
            'focus-visible:ring-2 focus-visible:ring-(--brand-primary) focus-visible:outline-none',
            active
              ? 'bg-(--surface) text-(--text-primary) shadow-sm'
              : 'text-(--text-secondary) hover:text-(--text-primary)'
          )}
        >
          {option.label}
        </button>
      )
    })}
  </div>
)

/** Liga e desliga uma coisa só: negrito, itálico, sublinhado. */
export const ToggleButton = ({
  label,
  pressed,
  onChange,
  children,
}: {
  label: string
  pressed: boolean
  onChange: (pressed: boolean) => void
  children: ReactNode
}): ReactNode => (
  <button
    type="button"
    title={label}
    aria-label={label}
    aria-pressed={pressed}
    onClick={() => {
      onChange(!pressed)
    }}
    className={cn(
      'flex h-8 w-8 items-center justify-center rounded-(--radius-md) border transition-colors duration-(--duration-fast)',
      'focus-visible:ring-2 focus-visible:ring-(--brand-primary) focus-visible:outline-none',
      pressed
        ? 'border-(--brand-primary) bg-(--brand-primary)/10 text-(--brand-primary)'
        : 'border-(--border-default) text-(--text-secondary) hover:text-(--text-primary)'
    )}
  >
    {children}
  </button>
)

export const ColorField = ({
  label,
  value,
  onChange,
}: {
  label: string
  value: string
  onChange: (value: string) => void
}): ReactNode => {
  const id = useId()
  return (
    <div className="flex items-center gap-2">
      <input
        id={id}
        type="color"
        value={value}
        onChange={(event) => {
          onChange(event.target.value.toLowerCase())
        }}
        className="h-8 w-10 shrink-0 cursor-pointer rounded-(--radius-md) border border-(--border-default) bg-(--surface) p-0.5"
      />
      <label htmlFor={id} className="flex flex-col text-(length:--text-xs)">
        <span className="text-(--text-secondary)">{label}</span>
        <span className="font-mono text-(--text-muted)">{value}</span>
      </label>
    </div>
  )
}
